import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import { PhoneMediaApi } from '@frontend/api-phone-media';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate, useSettingsMatch, useNavigationBlocker } from '@frontend/settings-routing';
import { FormFieldActionTypes, SkeletonLoader, useForm, useModalControl, useAlert } from '@frontend/design-system';
import { AudioItem } from '../../components/audio-picker/types';
import { CallQueuePage } from '../../components/call-queue/new/call-queue-page';
import { DeleteCallQueueButton } from '../../components/call-queue/new/delete-button';
import { populateSchema, createSubmitPayload } from '../../components/call-queue/new/schema';
import { CallQueueState } from '../../components/call-queue/new/types';
import { useCallQueueMutations } from '../../components/call-queue/new/use-call-queue-mutations';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { CancelEditModal } from '../../utils/cancel-edit.modal';
import { logError } from '../../utils/loggers';

const CallQueueDetailContent = ({
  data,
  tenantLocation,
  callRoute,
}: {
  data: CallQueueState;
  tenantLocation: SettingsTenantLocation;
  callRoute: { id?: string; name?: string; nodeId?: string };
}) => {
  const { t } = useTranslation('phone');
  const queryClient = useQueryClient();
  const { navigate } = useSettingsNavigate();
  const alerts = useAlert();
  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const { update } = useCallQueueMutations(tenantLocation.phoneTenantId ?? '');

  const populatedSchema = useMemo(() => {
    /**
     * We initialize the holdMusicSystemMedia field based on the holdMusicId value
     * */
    const holdMusicData = queryClient.getQueryData<{ standardMedia: AudioItem[] }>(
      [
        tenantLocation.phoneTenantId,
        ...queryKeys.audioPicker({
          add: true,
          custom: true,
          standard: true,
          tenantId: tenantLocation.phoneTenantId,
        }),
      ],
      {}
    )?.standardMedia;

    const flattened = holdMusicData ? Object.values(holdMusicData).flat() : [];
    const holdMusicSystemMedia = flattened.some((media) => media.id === data.holdMusicId);

    return populateSchema({ ...data, holdMusicSystemMedia });
  }, [data, queryClient, tenantLocation.phoneTenantId]);

  const form = useForm({
    fields: populatedSchema,
    computeChangedValues: true,
    /**
     * This reducer sets the holdMusicSystemMedia value based on the holdMusicId value
     * The holdMusicSystemMedia field is hidden to the user
     * */
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'holdMusicId') {
        const holdMusicData = queryClient.getQueryData<{ standardMedia: AudioItem[] }>(
          [
            tenantLocation.phoneTenantId,
            ...queryKeys.audioPicker({
              add: true,
              custom: true,
              standard: true,
              tenantId: tenantLocation.phoneTenantId,
            }),
          ],
          {}
        )?.standardMedia;
        const flattened = holdMusicData ? Object.values(holdMusicData).flat() : [];
        const holdMusicSystemMedia = flattened.some((media) => media.id === action.payload.value);

        return {
          ...state,
          holdMusicSystemMedia: { ...state.holdMusicSystemMedia, value: holdMusicSystemMedia },
        };
      }
      return state;
    },
  });
  const field = form.getFieldProps('name');

  const hasUnsavedChanges = useMemo(() => {
    return !!form.changedValues;
  }, [form.changedValues]);

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelModalProps.show) {
          cancelTriggerProps.onClick();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelModalProps.show]
  );

  const handleSave = useCallback(async () => {
    if (!tenantLocation.phoneTenantId) {
      return;
    }

    const finalState = createSubmitPayload({
      state: data,
      formValues: form.values,
    });

    const holdMusicItems = queryClient.getQueryData<{ standardMedia: AudioItem[] }>(
      [
        tenantLocation.phoneTenantId,
        ...queryKeys.audioPicker({
          add: true,
          custom: true,
          standard: true,
          tenantId: tenantLocation.phoneTenantId,
        }),
      ],
      {}
    )?.standardMedia;
    const flattened = holdMusicItems ? Object.values(holdMusicItems).flat() : [];
    const holdMusicSystemMedia = flattened.some((media) => media.id === form.values.holdMusicId);

    finalState.holdMusicSystemMedia = holdMusicSystemMedia;

    await new Promise<void>((resolve, reject) => {
      update.mutate(finalState, {
        onSuccess: () => {
          alerts.success(t('Call Queue updated successfully.'));
          const newFields = populateSchema(finalState);
          form.reset(newFields);
          resolve();
        },
        onError: (error) => {
          alerts.error(t('System failed to update Call Queue. Please try again.'));
          logError('CallQueueDetail:handleSave | Failed to update call queue', {
            data: {
              callQueueId: data.callQueueId,
              tenantId: tenantLocation.phoneTenantId,
              callRouteId: callRoute.id,
              error,
            },
          });
          reject(new Error('Failed to update call queue'));
        },
      });
    });
  }, [data, form, tenantLocation.phoneTenantId]);

  return (
    <>
      <PhoneSettingsPage
        renderContainer={false}
        hideLocationChip
        title={<EditableText alwaysShowIcon isTitle fieldProps={field} />}
        subtitle={t('Choose a ring option, assign devices, and customize the queue experience for callers on hold.')}
        action={
          data && tenantLocation.phoneTenantId ? (
            <DeleteCallQueueButton phoneTenantId={tenantLocation.phoneTenantId} callQueue={data} />
          ) : null
        }
        breadcrumbs={
          callRoute.id
            ? [
                { label: t('Phone'), to: '/phone/main' },
                { label: t('Call Routes'), to: '/phone/call-routes' },
                {
                  label: callRoute.name ?? '',
                  onClick: () =>
                    navigate({
                      to: '/phone/call-routes/:id',
                      params: { id: callRoute.id ?? '' },
                      context: { contextNodeId: callRoute.nodeId },
                    }),
                },
                { label: data?.name ?? '', to: '/phone/call-queues/:id', params: { id: data?.callQueueId ?? '' } },
              ]
            : [
                { label: t('Phone'), to: '/phone/main' },
                { label: t('Call Queues'), to: '/phone/call-queues' },
                { label: data?.name ?? '', to: '/phone/call-queues/:id', params: { id: data?.callQueueId ?? '' } },
              ]
        }
      >
        <CallQueuePage data={data} tenantLocation={tenantLocation} form={form} onSave={handleSave} />
      </PhoneSettingsPage>

      <CancelEditModal
        disabled={false}
        modalProps={cancelModalProps}
        onQuitClick={proceed}
        onSaveAndQuitClick={() => handleSave().then(() => proceed())}
      />
    </>
  );
};

export const CallQueueDetail = () => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { params, search } = useSettingsMatch<{
    Params: { id: string };
    Search: { crid?: string; crn?: string; nid?: string };
  }>();
  const { navigate } = useSettingsNavigate();
  const { data, isLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.readCallQueue(params.id)],
    queryFn: () => CallQueueAPI.Read({ callQueueId: params.id }),
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'CallQueueDetail:Read | Failed to fetch call queue',
        data: { callQueueId: params.id, tenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  // crid, crn and nid are set in the search params when the user is redirected from the call route details page.
  const [callRouteId, callRouteName, nodeId] = [search.crid, search.crn, search.nid];

  /**
   * This query fetches the hold music data for use in the form.
   * We don't actually need the data at this point.
   * */
  const { isSuccess } = useQuery({
    queryKey: [settingsTenantLocation?.id, ...queryKeys.holdMusic()],
    queryFn: () => {
      return PhoneMediaApi.listHoldMusic({ locationId: settingsTenantLocation?.id });
    },
    enabled: !!settingsTenantLocation?.id,
    meta: {
      error: {
        message: 'CallQueueDetail:HoldMusic | Failed to fetch hold music data',
        data: { tenantLocationId: settingsTenantLocation?.id },
      },
    },
  });
  const { t } = useTranslation('phone');

  if (data && isSuccess && settingsTenantLocation) {
    return (
      <CallQueueDetailContent
        data={data}
        tenantLocation={settingsTenantLocation}
        callRoute={{ id: callRouteId, name: callRouteName, nodeId }}
      />
    );
  }

  return (
    <PhoneSettingsPage
      title={'Call Queue'}
      subtitle={t('Choose a ring option, assign devices, and customize the queue experience for callers on hold.')}
      breadcrumbs={
        callRouteId
          ? [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Call Routes'), to: '/phone/call-routes' },
              {
                label: callRouteName ?? '',
                onClick: () =>
                  navigate({
                    to: '/phone/call-routes/:id',
                    params: { id: callRouteId },
                    context: { contextNodeId: nodeId },
                  }),
              },
              { label: data?.name ?? '', to: '/phone/call-queues/:id', params: { id: data?.callQueueId ?? '' } },
            ]
          : [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Call Queues'), to: '/phone/call-queues' },
              { label: data?.name ?? '', to: '/phone/call-queues/:id', params: { id: data?.callQueueId ?? '' } },
            ]
      }
    >
      {isLoading ? <SkeletonLoader count={2} height={300} distance={10} /> : t('No Call Queue found')}
    </PhoneSettingsPage>
  );
};
